import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  List,
  message,
  Modal,
  Select,
  Typography,
} from "antd";
import {
  PlusOutlined,
  CloudDownloadOutlined,
  WalletOutlined,
  CreditCardOutlined,
  BankOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import axios from "axios";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { BASE_API_URL } from "../../../../utils/consts";
import { isSuperAdmin } from "../../../../utils/permissions";
import { usePermissions } from "../../../../utils/usePermissions";
import SettingsHeader from "../../Components/SettingsHeader/SettingsHeader";
import ManageExtraIncomeModal from "../../../../Components/Modals/ManageExtraIncomeModal/ManageExtraIncomeModal";
import { applyRevenueTypeOrder } from "../DailyReventuSettings";
import "./DailyRevenueDetails.scss";
import {
  createDailyRevenueSupplierExpense,
  getDailyRevenueSupplierExpenses,
  deleteDailyRevenueSupplierExpense,
  DailyRevenueSupplierExpense,
} from "../../../../utils/api/supplierExpensesAPI";
import { isLocationActive } from "../../../../utils/helpers/locationVisibility";

const { Option } = Select;
const { Text } = Typography;

type Props = {};

const DailyRevenueDetails: React.FC<Props> = () => {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userData = useSelector((state: any) => state.userData.user);
  const { hasPermission } = usePermissions();
  const canViewSupplierExpenses =
    isSuperAdmin(userData?.roles) ||
    hasPermission("finance:view-supplier-expenses");

  const [dailyRevenueDetails, setDailyRevenueDetails] =
    useState<any>(undefined);
  const [cashRegisterList, setCashRegisterList] = useState<
    { description: string; total: string | number; other?: boolean }[]
  >([]);
  const [revenueTypes, setRevenueTypes] = useState<any>(undefined);
  const [revenueTypeValues, setRevenueTypeValues] = useState<
    Record<string, number>
  >({});
  const [expenseCategories, setExpenseCategories] = useState<any[]>([]);
  const [expenseMatrixEntries, setExpenseMatrixEntries] = useState<any[]>([]);
  const [newExpenseMatrixEntry, setNewExpenseMatrixEntry] = useState({
    categoryId: "",
    supplierId: "",
    amount: "",
    other: false,
  });

  const [newExpense, setNewExpense] = useState({
    description: "",
    total: "",
    other: false,
  });
  const [locations, setLocations] = useState<any>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [isExtraIncomeModalOpen, setIsExtraIncomeModalOpen] = useState(false);
  const [extraIncomeCount, setExtraIncomeCount] = useState<number>(0);
  const [octopusLoading, setOctopusLoading] = useState(false);
  const [octopusModalOpen, setOctopusModalOpen] = useState(false);
  const [octopusData, setOctopusData] = useState<{
    cashAmount?: number;
    cardAmount?: number;
    bankTransferAmount?: number;
    otherAmount?: number;
  } | null>(null);
  const [supplierExpenses, setSupplierExpenses] = useState<
    DailyRevenueSupplierExpense[]
  >([]);
  const [tempSupplierExpenses, setTempSupplierExpenses] = useState<any[]>([]);
  const [newSupplierExpense, setNewSupplierExpense] = useState({
    categoryId: "",
    supplierId: "",
    amount: "",
    other: false,
  });

  const [form] = Form.useForm();
  const isEditing = params.id !== "new";
  const watchDate = Form.useWatch("date", form);
  const watchLocation = Form.useWatch("location", form);
  const watchCash = Form.useWatch("totalRevenueCash", form);
  const watchCard = Form.useWatch("totalRevenueCard", form);
  const watchBank = Form.useWatch("bankTransfer", form);
  const watchInternal = Form.useWatch("internalCash", form);
  const watchDelivery = Form.useWatch("delivery", form);
  const watchDrugo = Form.useWatch("drugo", form);

  const paymentChannelsTotal = useMemo(() => {
    return (
      (Number(watchCash) || 0) +
      (Number(watchCard) || 0) +
      (Number(watchBank) || 0) +
      (Number(watchInternal) || 0) +
      (Number(watchDelivery) || 0) +
      (Number(watchDrugo) || 0)
    );
  }, [watchCash, watchCard, watchBank, watchInternal, watchDelivery, watchDrugo]);

  const revenueByTypeTotal = useMemo(() => {
    return Object.values(revenueTypeValues).reduce(
      (sum, v) => sum + (Number(v) || 0),
      0,
    );
  }, [revenueTypeValues]);

  // Memoize dateRange to prevent infinite loop in ManageExtraIncomeModal
  const extraIncomeDateRange = useMemo(() => {
    return [watchDate, watchDate] as [any, any];
  }, [watchDate]);

  const fetchExtraIncomeCount = async () => {
    const date = form.getFieldValue("date");
    const location = form.getFieldValue("location");

    if (!userData?.clientId || !date || !location) {
      setExtraIncomeCount(0);
      return;
    }

    try {
      const response = await axios.get(`${BASE_API_URL}/extra-income`, {
        params: {
          clientId: userData.clientId,
          startDate: date.format("YYYY-MM-DD"),
          endDate: date.format("YYYY-MM-DD"),
          locationId: location,
        },
      });
      setExtraIncomeCount(response.data.entries?.length || 0);
    } catch (error) {
      console.error("Error fetching extra income count:", error);
      setExtraIncomeCount(0);
    }
  };

  useEffect(() => {
    if (userData?.clientId && params.id !== "new") {
      axios
        .get(
          `${BASE_API_URL}/admin/${userData?.clientId}/getRevenue/${params.id}`,
        )
        .then((res) => {
          setDailyRevenueDetails(res.data);
        })
        .catch((err: any) => console.error(err));
    }
  }, [userData, params.id]);

  useEffect(() => {
    if (dailyRevenueDetails && revenueTypes?.length > 0) {
      form.setFieldsValue({
        date: dayjs(dailyRevenueDetails.date),
        totalRevenueCash: dailyRevenueDetails.cashAmount,
        totalRevenueCard: dailyRevenueDetails.cardAmount,
        bankTransfer: dailyRevenueDetails.bankTransfer,
        internalCash: dailyRevenueDetails.cash,
        delivery: dailyRevenueDetails.delivery,
        drugo: dailyRevenueDetails.drugo,
        notes: dailyRevenueDetails.notes,
        location: dailyRevenueDetails.locationId?._id,
      });

      const revenueValues: Record<string, number> = {};
      dailyRevenueDetails.revenueByType?.forEach((item: any) => {
        // Handle both populated object and string ID
        const typeId =
          typeof item.revenueTypeId === "object"
            ? item.revenueTypeId?._id
            : item.revenueTypeId;
        if (typeId) {
          revenueValues[typeId] = item.totalAmount;
        }
      });
      setRevenueTypeValues(revenueValues);

      setCashRegisterList(
        dailyRevenueDetails.paymentFromTheRevenue?.map((item: any) => ({
          description: item.description,
          total: item.total,
          other: item.other || false,
        })) || [],
      );
      setExpenseMatrixEntries(
        dailyRevenueDetails.expenseMatrixEntries?.map((item: any) => ({
          categoryId: item.categoryId?._id || item.categoryId || "",
          supplierId: item.supplierId?._id || item.supplierId || "",
          amount: item.amount ?? "",
          other: !!item.other,
          paymentStatus: item.paymentStatus || "paid",
          partialPayment: item.partialPayment || {
            paidAmount: 0,
            remainingAmount: 0,
          },
        })) || [],
      );
    }
  }, [dailyRevenueDetails, revenueTypes, form]);

  useEffect(() => {
    if (!userData?.clientId) return;

    axios
      .get(`${BASE_API_URL}/${userData?.clientId}/locations`)
      .then((res) => {
        setLocations((res.data || []).filter(isLocationActive));
      })
      .catch((err) => console.error(err));

    axios
      .get(`${BASE_API_URL}/admin/${userData.clientId}/getRevenueTypes`)
      .then((res) => {
        setRevenueTypes(applyRevenueTypeOrder(res.data || []));
      })
      .catch((error) => console.error(error));

    axios
      .get(`${BASE_API_URL}/admin/expense-categories`, {
        params: { clientId: userData?.clientId },
      })
      .then((res) => {
        setExpenseCategories(res.data?.categories || []);
      })
      .catch((err) => console.error(err));
  }, [userData]);

  useEffect(() => {
    form.setFieldsValue({
      date: dayjs(),
    });
  }, [form]);

  // Fetch extra income count when date or location changes
  useEffect(() => {
    fetchExtraIncomeCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchDate, watchLocation]);

  // Fetch supplier expenses when editing existing daily revenue
  useEffect(() => {
    if (!userData?.clientId || params.id === "new") return;

    const fetchSupplierExpenses = async () => {
      try {
        const response = await getDailyRevenueSupplierExpenses({
          clientId: userData.clientId,
          dailyRevenueId: params.id,
        });
        setSupplierExpenses(response.data || []);
      } catch (error) {
        console.error("Error fetching supplier expenses:", error);
        setSupplierExpenses([]);
      }
    };

    fetchSupplierExpenses();
  }, [userData?.clientId, params.id]);

  const onFinish = async (values: any) => {
    if (!userData?.clientId) return;
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    // Validate that all revenue types have a value (0 is acceptable)
    if (revenueTypes?.length > 0) {
      const missing = revenueTypes.filter(
        (type: any) => revenueTypeValues[type._id] === undefined || revenueTypeValues[type._id] === null,
      );
      if (missing.length > 0) {
        message.error(t("Unesite vrijednost za sve grupe prihoda (0 ako nema prihoda)"));
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
    }

    const formattedRevenueTypes = (revenueTypes || []).map((type: any) => ({
      revenueTypeId: type._id,
      name: type.name,
      totalAmount: revenueTypeValues[type._id] || 0,
    }));

    const formattedDate = values.date
      ? dayjs(values.date).startOf("day").format("YYYY-MM-DDTHH:mm:ss")
      : undefined;

    const payload = {
      locationId: values.location,
      date: formattedDate,
      cashAmount: values.totalRevenueCash,
      cardAmount: values.totalRevenueCard,
      notes: values.notes,
      paymentFromTheRevenue: cashRegisterList,
      revenueByType: formattedRevenueTypes,
      bankTransfer: values.bankTransfer,
      cash: values.internalCash,
      delivery: values.delivery,
      drugo: values.drugo,
      expenseMatrixEntries: expenseMatrixEntries
        .filter(
          (item: any) => item.categoryId && item.supplierId && item.amount,
        )
        .map((item: any) => ({
          categoryId: item.categoryId,
          supplierId: item.supplierId,
          amount: Number(item.amount),
          other: !!item.other,
          paymentStatus: item.paymentStatus || "paid",
          partialPayment: item.partialPayment || {
            paidAmount: Number(item.amount) || 0,
            remainingAmount: 0,
          },
        })),
    };

    try {
      if (params.id === "new") {
        const response = await axios.post(
          `${BASE_API_URL}/admin/${userData?.clientId}/createRevenue`,
          payload,
        );

        // Save temporary supplier expenses to new table
        const dailyRevenueId = response.data._id || response.data.id;
        if (dailyRevenueId && tempSupplierExpenses.length > 0) {
          for (const expense of tempSupplierExpenses) {
            try {
              await createDailyRevenueSupplierExpense({
                clientId: userData.clientId,
                dailyRevenueId: dailyRevenueId,
                locationId: values.location,
                categoryId: expense.categoryId,
                supplierId: expense.supplierId,
                supplierName: expense.supplierName,
                amount: expense.amount,
                date: formattedDate || dayjs().format("YYYY-MM-DD"),
                description: "",
                other: expense.other || false,
                paymentStatus: "paid" as const,
                partialPayment: {
                  paidAmount: expense.amount,
                  remainingAmount: 0,
                },
              });
            } catch (expenseError) {
              console.error("Error saving supplier expense:", expenseError);
            }
          }
        }
      } else {
        await axios.put(
          `${BASE_API_URL}/admin/${userData?.clientId}/updateRevenue/${params.id}`,
          payload,
        );
      }

      navigate("/dailyRevenue");
    } catch (error) {
      console.error("Error saving revenue:", error);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleImportFromOctopus = async () => {
    const date = form.getFieldValue("date");
    const location = form.getFieldValue("location");
    if (!date || !location) {
      Modal.warning({
        title: t("Missing Information"),
        content: t("Please select date and location first"),
        okText: t("OK"),
      });
      return;
    }

    setOctopusLoading(true);
    try {
      const response = await axios.get(
        `${BASE_API_URL}/${userData?.clientId}/octopus-tokens/daily-report`,
        {
          params: {
            locationId: location,
            date: dayjs(date).format("YYYY-MM-DD"),
          },
        },
      );
      setOctopusData({
        cashAmount: response.data?.cashAmount,
        cardAmount: response.data?.cardAmount,
        bankTransferAmount: response.data?.bankTransferAmount,
        otherAmount: response.data?.otherAmount,
      });
      setOctopusModalOpen(true);
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          t("Failed to fetch data from Octopus"),
      );
    } finally {
      setOctopusLoading(false);
    }
  };

  const confirmOctopusImport = () => {
    if (!octopusData) return;
    // Only fill in fields Octopus actually returned a value for — leave the
    // rest untouched so the user can still fill them in manually.
    const values: Record<string, number> = {};
    if (octopusData.cashAmount !== undefined) values.totalRevenueCash = octopusData.cashAmount;
    if (octopusData.cardAmount !== undefined) values.totalRevenueCard = octopusData.cardAmount;
    if (octopusData.bankTransferAmount !== undefined) values.bankTransfer = octopusData.bankTransferAmount;
    if (octopusData.otherAmount !== undefined) values.drugo = octopusData.otherAmount;
    form.setFieldsValue(values);
    setOctopusModalOpen(false);
  };

  const addExpense = () => {
    if (!newExpense.description || !newExpense.total) return;
    setCashRegisterList((prev: any) => [...prev, newExpense]);
    setNewExpense({ description: "", total: "", other: false });
  };

  const removeExpense = (index: number) => {
    setCashRegisterList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRevenueTypeChange = (id: string, value: string) => {
    setRevenueTypeValues((prev) => ({
      ...prev,
      [id]: Number(value),
    }));
  };

  const addExpenseMatrixEntry = () => {
    // Supplier and amount are required, category is optional
    if (!newExpenseMatrixEntry.supplierId || !newExpenseMatrixEntry.amount) {
      return;
    }

    setExpenseMatrixEntries((prev) => [
      ...prev,
      {
        categoryId: newExpenseMatrixEntry.categoryId || null,
        supplierId: newExpenseMatrixEntry.supplierId,
        amount: newExpenseMatrixEntry.amount,
        other: newExpenseMatrixEntry.other,
        // Set as "paid" by default - these expenses are paid through daily revenue
        paymentStatus: "paid",
        partialPayment: {
          paidAmount: Number(newExpenseMatrixEntry.amount) || 0,
          remainingAmount: 0,
        },
      },
    ]);

    setNewExpenseMatrixEntry({
      categoryId: "",
      supplierId: "",
      amount: "",
      other: false,
    });
  };

  const removeExpenseMatrixEntry = (index: number) => {
    setExpenseMatrixEntries((prev) => prev.filter((_, i) => i !== index));
  };

  // ========================================
  // NEW: Supplier Expense Functions (for new table)
  // ========================================

  const addSupplierExpense = async () => {
    if (!newSupplierExpense.supplierId || !newSupplierExpense.amount) {
      return;
    }

    const date = form.getFieldValue("date");
    const locationId = form.getFieldValue("location");

    if (!userData?.clientId || !date || !locationId) {
      Modal.error({
        title: t("Error"),
        content: t("Please select date and location first"),
      });
      return;
    }

    // Find supplier name
    const allSuppliersFlat = expenseCategories.flatMap(
      (cat: any) =>
        cat.suppliers?.map((s: any) => ({
          ...s,
          categoryId: cat._id,
          categoryName: cat.name,
        })) || [],
    );
    const supplier = allSuppliersFlat.find(
      (s: any) => s._id === newSupplierExpense.supplierId,
    );

    try {
      if (params.id !== "new" && params.id) {
        // For editing existing daily revenue, save immediately
        const expenseData = {
          clientId: userData.clientId,
          dailyRevenueId: params.id,
          locationId: locationId,
          categoryId:
            newSupplierExpense.categoryId || supplier?.categoryId || "",
          supplierId: newSupplierExpense.supplierId,
          supplierName: supplier?.name || "Unknown",
          amount: Number(newSupplierExpense.amount),
          date: dayjs(date).format("YYYY-MM-DD"),
          description: "",
          other: newSupplierExpense.other,
          paymentStatus: "paid" as const,
          partialPayment: {
            paidAmount: Number(newSupplierExpense.amount) || 0,
            remainingAmount: 0,
          },
        };

        const response = await createDailyRevenueSupplierExpense(expenseData);
        setSupplierExpenses((prev) => [...prev, response.data]);
      } else {
        // For new daily revenue, store in temporary state
        const tempExpense = {
          categoryId:
            newSupplierExpense.categoryId || supplier?.categoryId || "",
          supplierId: newSupplierExpense.supplierId,
          supplierName: supplier?.name || "Unknown",
          amount: Number(newSupplierExpense.amount),
          categoryName: supplier?.categoryName || "",
          other: newSupplierExpense.other,
        };
        setTempSupplierExpenses((prev) => [...prev, tempExpense]);
      }

      setNewSupplierExpense({
        categoryId: "",
        supplierId: "",
        amount: "",
        other: false,
      });
    } catch (error) {
      console.error("Error adding supplier expense:", error);
      Modal.error({
        title: t("Error"),
        content: t("Failed to add supplier expense"),
      });
    }
  };

  const removeSupplierExpense = async (
    expenseId?: string,
    tempIndex?: number,
  ) => {
    if (expenseId) {
      // Remove from database (edit mode)
      try {
        await deleteDailyRevenueSupplierExpense(expenseId);
        setSupplierExpenses((prev) =>
          prev.filter((exp) => exp._id !== expenseId),
        );
      } catch (error) {
        console.error("Error deleting supplier expense:", error);
        Modal.error({
          title: t("Error"),
          content: t("Failed to delete supplier expense"),
        });
      }
    } else if (tempIndex !== undefined) {
      // Remove from temporary state (new mode)
      setTempSupplierExpenses((prev) => prev.filter((_, i) => i !== tempIndex));
    }
  };

  // ========================================
  // END: Supplier Expense Functions
  // ========================================

  const selectedCategory = expenseCategories.find(
    (cat) => cat._id === newExpenseMatrixEntry.categoryId,
  );

  // Get all suppliers from all categories
  const allSuppliers = React.useMemo(() => {
    const suppliersMap = new Map();
    expenseCategories.forEach((cat: any) => {
      cat.suppliers?.forEach((sup: any) => {
        if (!suppliersMap.has(sup._id)) {
          suppliersMap.set(sup._id, {
            ...sup,
            categoryId: cat._id,
            categoryName: cat.name,
          });
        }
      });
    });
    return Array.from(suppliersMap.values());
  }, [expenseCategories]);

  // Get suppliers to display based on selected category
  const displayedSuppliers = React.useMemo(() => {
    if (newExpenseMatrixEntry.categoryId && selectedCategory?.suppliers) {
      // If category selected, show only suppliers from that category
      return selectedCategory.suppliers;
    }
    // If no category selected, show all suppliers
    return allSuppliers;
  }, [newExpenseMatrixEntry.categoryId, selectedCategory, allSuppliers]);

  return (
    <div className="dailyRevenueDetailsPage">
      <SettingsHeader
        title={isEditing ? t("Edit daily revenue") : t("New daily revenue")}
        onBack={() => navigate(-1)}
      />

      <Form
        name="fixedPaymentForm"
        className="dailyRevenueForm"
        layout="vertical"
        form={form}
        style={{ maxWidth: "100%" }}
        onFinish={onFinish}
        autoComplete="off"
      >
        <div className="dailyRevenueHeroCard">
          <div>
            <span className="pill">
              {isEditing ? t("Edit mode") : t("Create mode")}
            </span>
            <div className="dailyRevenueHeroCard__title">
              {t("Daily revenue")}
            </div>
            <p className="dailyRevenueHeroCard__subtitle">
              {t(
                "Capture totals, cash outs and revenue types in one cozy layout.",
              )}
            </p>
          </div>
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: "auto",
              alignItems: "flex-start",
            }}
          >
            {!isEditing && (
              <Button
                type="default"
                icon={<CloudDownloadOutlined />}
                loading={octopusLoading}
                onClick={handleImportFromOctopus}
              >
                {t("Import from Octopus")}
              </Button>
            )}
            <Badge count={extraIncomeCount} showZero={false} offset={[-5, 5]}>
              <Button
                type="default"
                icon={<PlusOutlined />}
                onClick={() => {
                  const date = form.getFieldValue("date");
                  const location = form.getFieldValue("location");
                  if (!date || !location) {
                    Modal.warning({
                      title: t("Missing Information"),
                      content: t("Please select date and location first"),
                      okText: t("OK"),
                    });
                    return;
                  }
                  setIsExtraIncomeModalOpen(true);
                }}
              >
                {t("Extra Income")}
              </Button>
            </Badge>
          </div>
        </div>

        <Modal
          title={
            <span className="octopusImportModal__titleRow">
              <span className="octopusImportModal__titleIcon">
                <CloudDownloadOutlined />
              </span>
              {t("Import from Octopus")}
            </span>
          }
          open={octopusModalOpen}
          onOk={confirmOctopusImport}
          onCancel={() => setOctopusModalOpen(false)}
          okText={t("Confirm")}
          cancelText={t("Cancel")}
          className="octopusImportModal"
          width={480}
          centered
        >
          <p className="octopusImportModal__description">
            {t(
              "The following amounts were found. Confirm to fill in the fields below.",
            )}
          </p>
          <div className="octopusImportModal__rows">
            {[
              {
                key: "cash",
                label: t("Total revenue cash"),
                value: octopusData?.cashAmount,
                icon: <WalletOutlined />,
              },
              {
                key: "card",
                label: t("Total revenue card"),
                value: octopusData?.cardAmount,
                icon: <CreditCardOutlined />,
              },
              {
                key: "bankTransfer",
                label: t("Bank Transfer"),
                value: octopusData?.bankTransferAmount,
                icon: <BankOutlined />,
              },
              {
                key: "other",
                label: t("Other"),
                value: octopusData?.otherAmount,
                icon: <MoreOutlined />,
              },
            ]
              .filter((row) => row.value !== undefined)
              .map((row) => (
              <div className="octopusImportModal__row" key={row.key}>
                <span
                  className={`octopusImportModal__icon octopusImportModal__icon--${row.key}`}
                >
                  {row.icon}
                </span>
                <span className="octopusImportModal__info">
                  <span className="octopusImportModal__label">
                    {row.label}
                  </span>
                  <span className="octopusImportModal__value">
                    {(row.value || 0).toLocaleString("sr-RS")} RSD
                  </span>
                </span>
              </div>
            ))}
          </div>
        </Modal>

        <div className="dailyRevenueCard">
          <div className="dailyRevenueCard__header">
            <div>
              <div className="dailyRevenueCard__eyebrow">{t("Start")}</div>
              <div className="dailyRevenueCard__title">
                {t("Date and location")}
              </div>
              <p className="dailyRevenueCard__description">
                {t("Pair the shift date with the venue you're tracking.")}
              </p>
            </div>
          </div>

          <div className="fieldGrid">
            <Form.Item
              name="date"
              label={t("Date")}
              rules={[{ required: true, message: t("Please select date!") }]}
            >
              <DatePicker
                className="modern-range-picker single-month-picker"
                placeholder={t("Select date")}
              />
            </Form.Item>

            <Form.Item
              name="location"
              label={t("Location")}
              rules={[
                { required: true, message: t("Please select location!") },
              ]}
            >
              <Select placeholder={t("Select location")}>
                {locations?.map((item: any, index: number) => {
                  return (
                    <Option value={item._id} key={index}>
                      {item.name}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </div>
        </div>

        <div className="dailyRevenueCard">
          <div className="dailyRevenueCard__header">
            <div>
              <div className="dailyRevenueCard__eyebrow">{t("Totals")}</div>
              <div className="dailyRevenueCard__title">
                {t("Revenue channels")}
              </div>
              <p className="dailyRevenueCard__description">
                {t("Split how the money came in to keep reports clean.")}
              </p>
            </div>
          </div>

          <div className="fieldGrid">
            <Form.Item
              name="totalRevenueCash"
              label={t("Total revenue cash")}
              rules={[
                {
                  required: false,
                  message: t("Please enter total revenue cash!"),
                },
              ]}
            >
              <Input
                type="number"
                placeholder={t("Enter total revenue cash")}
              />
            </Form.Item>

            <Form.Item
              name="totalRevenueCard"
              label={t("Total revenue card")}
              rules={[
                {
                  required: false,
                  message: t("Please enter total revenue card!"),
                },
              ]}
            >
              <Input
                type="number"
                placeholder={t("Enter total revenue card")}
              />
            </Form.Item>

            <Form.Item
              name="bankTransfer"
              label={t("Bank Transfer")}
              rules={[
                { required: false, message: t("Please enter bank transfer!") },
              ]}
            >
              <Input
                type="number"
                placeholder={t("Enter total bank transfer")}
              />
            </Form.Item>

            <Form.Item
              name="internalCash"
              label={t("Internal cash")}
              rules={[
                {
                  required: false,
                  message: t("Please enter  internal cash!"),
                },
              ]}
            >
              <Input
                type="number"
                placeholder={t("Enter total internal cash")}
              />
            </Form.Item>

            <Form.Item
              name="delivery"
              label={t("Delivery")}
              rules={[
                { required: false, message: t("Please enter  delivery!") },
              ]}
            >
              <Input type="number" placeholder={t("Enter total delivery")} />
            </Form.Item>

            <Form.Item
              name="drugo"
              label={t("Other")}
              rules={[{ required: false }]}
            >
              <Input type="number" placeholder={t("Enter other amount")} />
            </Form.Item>
          </div>
        </div>

        <div className="dailyRevenueCard dailyRevenueCard--legacy" style={{ display: "none" }}>
          <div className="dailyRevenueCard__header">
            <div>
              <div className="dailyRevenueCard__eyebrow">
                {t("Legacy System")}
              </div>
              <div
                className="dailyRevenueCard__title"
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                {t("Operational & payouts")}
                <span className="legacyBadge">V1</span>
              </div>
              <p className="dailyRevenueCard__description">
                {t(
                  "Upiši troškove u matricu, a zatim po potrebi dodaj gotovinske isplate.",
                )}
              </p>
              <div className="legacyNotice">
                {t(
                  "Od 1. aprila obavezno je koristiti novi sistem ispod.",
                )}
              </div>
            </div>
          </div>

          {/* Matrix first */}
          <div className="matrixBlock">
            <Text strong>{t("Add to expense matrix (optional)")}</Text>
            <div className="matrixGrid">
              <Select
                showSearch
                allowClear
                placeholder={t("Category")}
                value={newExpenseMatrixEntry.categoryId || undefined}
                onChange={(value) =>
                  setNewExpenseMatrixEntry((prev) => ({
                    ...prev,
                    categoryId: value,
                    supplierId: "",
                  }))
                }
                filterOption={(input, option: any) => {
                  const category = expenseCategories.find(
                    (c: any) => c._id === option?.value,
                  );
                  if (!category) return false;
                  return category.name
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }}
              >
                {expenseCategories.map((cat: any) => (
                  <Option value={cat._id} key={cat._id}>
                    {cat.name}
                  </Option>
                ))}
              </Select>

              <Select
                showSearch
                allowClear
                placeholder={t("Supplier")}
                value={newExpenseMatrixEntry.supplierId || undefined}
                onChange={(value) => {
                  // Find the selected supplier
                  const supplier = displayedSuppliers.find(
                    (s: any) => s._id === value,
                  );

                  // Auto-populate category if not selected
                  const categoryId =
                    newExpenseMatrixEntry.categoryId || supplier?.categoryId;

                  setNewExpenseMatrixEntry((prev) => ({
                    ...prev,
                    supplierId: value,
                    categoryId: categoryId || prev.categoryId,
                  }));
                }}
                filterOption={(input, option: any) => {
                  const supplier = displayedSuppliers.find(
                    (s: any) => s._id === option?.value,
                  );
                  if (!supplier) return false;
                  return supplier.name
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }}
                notFoundContent={t("No suppliers found")}
              >
                {displayedSuppliers.map((sup: any) => (
                  <Option value={sup._id} key={sup._id}>
                    {sup.name}
                    {!newExpenseMatrixEntry.categoryId && sup.categoryName && (
                      <span
                        style={{
                          color: "#999",
                          fontSize: "12px",
                          marginLeft: "8px",
                        }}
                      >
                        ({sup.categoryName})
                      </span>
                    )}
                  </Option>
                ))}
              </Select>

              <Input
                type="number"
                placeholder={t("Amount (RSD)")}
                value={newExpenseMatrixEntry.amount}
                onChange={(e) =>
                  setNewExpenseMatrixEntry((prev) => ({
                    ...prev,
                    amount: e.target.value,
                  }))
                }
              />

              <Checkbox
                checked={newExpenseMatrixEntry.other}
                onChange={(e) =>
                  setNewExpenseMatrixEntry((prev) => ({
                    ...prev,
                    other: e.target.checked,
                  }))
                }
              >
                {t("Other (exclude from business)")}
              </Checkbox>

              <Button type="dashed" onClick={addExpenseMatrixEntry}>
                {t("Add")}
              </Button>
            </div>

            {expenseMatrixEntries.length > 0 && (
              <List
                bordered
                className="matrixList"
                dataSource={expenseMatrixEntries}
                renderItem={(item: any, index) => {
                  const category = expenseCategories.find(
                    (c) => c._id === item.categoryId,
                  );
                  const supplier = category?.suppliers?.find(
                    (s: any) => s._id === item.supplierId,
                  );

                  return (
                    <List.Item
                      actions={[
                        <Button
                          danger
                          size="small"
                          onClick={() => removeExpenseMatrixEntry(index)}
                        >
                          {t("Delete")}
                        </Button>,
                      ]}
                    >
                      <Text style={{ flex: 1 }}>
                        {category?.name || t("Unknown category")} /{" "}
                        {supplier?.name || t("Unknown supplier")} -{" "}
                        {item.amount} RSD{" "}
                        {item.other && (
                          <span className="textAccent">({t("Other")})</span>
                        )}
                      </Text>
                    </List.Item>
                  );
                }}
              />
            )}
          </div>
          <Divider />

          {/* Cash outs secondary */}
          <div className="cashOutComposer">
            <Input
              placeholder={t("Description")}
              value={newExpense.description}
              onChange={(e) =>
                setNewExpense((prev: any) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
            <Input
              type="number"
              placeholder={t("Amount (RSD)")}
              value={newExpense.total}
              onChange={(e) =>
                setNewExpense((prev: any) => ({
                  ...prev,
                  total: e.target.value,
                }))
              }
            />
            <Checkbox
              checked={newExpense.other}
              className="dailyRevenueOther"
              onChange={(e) =>
                setNewExpense((prev: any) => ({
                  ...prev,
                  other: e.target.checked,
                }))
              }
            >
              {t("Other")}
            </Checkbox>
            <Button type="dashed" onClick={addExpense}>
              {t("Add cash out")}
            </Button>
          </div>

          {cashRegisterList.length > 0 && (
            <List
              className="cashOutList"
              bordered
              dataSource={cashRegisterList}
              renderItem={(item: any, index) => (
                <List.Item
                  actions={[
                    <Button
                      danger
                      size="small"
                      onClick={() => removeExpense(index)}
                    >
                      {t("Delete")}
                    </Button>,
                  ]}
                >
                  <Text>
                    {item.description} - {item.total} RSD{" "}
                    {item?.other && (
                      <span className="textAccent">({t("Other")})</span>
                    )}
                  </Text>
                </List.Item>
              )}
            />
          )}
        </div>

        {/* NEW SECTION: Supplier Expenses (DailyRevenueSupplierExpense table) */}
        {canViewSupplierExpenses && (
          <div className="dailyRevenueCard dailyRevenueCard--new">
            <div className="dailyRevenueCard__header">
              <div>
                <div className="dailyRevenueCard__eyebrow">
                  {t("New System")}
                </div>
                <div
                  className="dailyRevenueCard__title"
                  style={{ display: "flex", alignItems: "center", gap: 10 }}
                >
                  {t("Supplier Expenses & Deductions")}
                  <span className="newSystemBadge">✦ New</span>
                </div>
                <p className="dailyRevenueCard__description">
                  {t(
                    "Prati troškove dobavljača i gotovinske odbojke — sve se čuva u novom sistemu.",
                  )}
                </p>
              </div>
            </div>

            <div className="newSystemSectionLabel">{t("Dobavljač")}</div>
            <div className="matrixBlock matrixBlock--new">
              <div className="matrixGrid">
                <Select
                  showSearch
                  allowClear
                  placeholder={t("Category (optional)")}
                  value={newSupplierExpense.categoryId || undefined}
                  onChange={(value) =>
                    setNewSupplierExpense((prev) => ({
                      ...prev,
                      categoryId: value || "",
                      supplierId: "",
                    }))
                  }
                  filterOption={(input, option: any) => {
                    const category = expenseCategories.find(
                      (c: any) => c._id === option?.value,
                    );
                    if (!category) return false;
                    return category.name
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }}
                >
                  {expenseCategories.map((cat: any) => (
                    <Option value={cat._id} key={cat._id}>
                      {cat.name}
                    </Option>
                  ))}
                </Select>

                <Select
                  showSearch
                  allowClear
                  placeholder={t("Supplier")}
                  value={newSupplierExpense.supplierId || undefined}
                  onChange={(value) => {
                    const allSuppliersFlat = expenseCategories.flatMap(
                      (cat: any) =>
                        cat.suppliers?.map((s: any) => ({
                          ...s,
                          categoryId: cat._id,
                          categoryName: cat.name,
                        })) || [],
                    );
                    const supplier = allSuppliersFlat.find(
                      (s: any) => s._id === value,
                    );

                    const categoryId =
                      newSupplierExpense.categoryId || supplier?.categoryId;

                    setNewSupplierExpense((prev) => ({
                      ...prev,
                      supplierId: value || "",
                      categoryId: categoryId || prev.categoryId,
                    }));
                  }}
                  filterOption={(input, option: any) => {
                    const allSuppliersFlat = expenseCategories.flatMap(
                      (cat: any) => cat.suppliers || [],
                    );
                    const supplier = allSuppliersFlat.find(
                      (s: any) => s._id === option?.value,
                    );
                    if (!supplier) return false;
                    return supplier.name
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }}
                  notFoundContent={t("No suppliers found")}
                >
                  {(() => {
                    const selectedCategory = expenseCategories.find(
                      (cat: any) => cat._id === newSupplierExpense.categoryId,
                    );
                    const suppliers = selectedCategory
                      ? selectedCategory.suppliers || []
                      : expenseCategories.flatMap(
                          (cat: any) =>
                            cat.suppliers?.map((s: any) => ({
                              ...s,
                              categoryName: cat.name,
                            })) || [],
                        );

                    return suppliers.map((sup: any) => (
                      <Option value={sup._id} key={sup._id}>
                        {sup.name}
                        {!newSupplierExpense.categoryId && sup.categoryName && (
                          <span
                            style={{
                              color: "#999",
                              fontSize: "12px",
                              marginLeft: "8px",
                            }}
                          >
                            ({sup.categoryName})
                          </span>
                        )}
                      </Option>
                    ));
                  })()}
                </Select>

                <Input
                  type="number"
                  placeholder={t("Amount (RSD)")}
                  value={newSupplierExpense.amount}
                  onChange={(e) =>
                    setNewSupplierExpense((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                />

                <Checkbox
                  checked={newSupplierExpense.other}
                  onChange={(e) =>
                    setNewSupplierExpense((prev) => ({
                      ...prev,
                      other: e.target.checked,
                    }))
                  }
                >
                  {t("Other (exclude from business)")}
                </Checkbox>

                <Button type="dashed" onClick={addSupplierExpense}>
                  {t("Add")}
                </Button>
              </div>

              {/* Display saved expenses (edit mode) */}
              {params.id !== "new" && supplierExpenses.length > 0 && (
                <List
                  bordered
                  className="matrixList"
                  dataSource={supplierExpenses}
                  renderItem={(item: DailyRevenueSupplierExpense) => {
                    // Handle both populated categoryId object and string ID
                    let categoryName = t("N/A");
                    if (item.categoryId) {
                      if (typeof item.categoryId === "string") {
                        const category = expenseCategories.find(
                          (c) => c._id === item.categoryId,
                        );
                        categoryName = category?.name || t("N/A");
                      } else {
                        // Populated object
                        categoryName =
                          (item.categoryId as any)?.name || t("N/A");
                      }
                    }

                    return (
                      <List.Item
                        actions={[
                          <Button
                            danger
                            size="small"
                            onClick={() => removeSupplierExpense(item._id)}
                          >
                            {t("Delete")}
                          </Button>,
                        ]}
                      >
                        <Text style={{ flex: 1 }}>
                          {categoryName} / {item.supplierName} - {item.amount}{" "}
                          RSD{" "}
                          {item.other && (
                            <span className="textAccent">({t("Other")})</span>
                          )}
                        </Text>
                      </List.Item>
                    );
                  }}
                />
              )}

              {/* Display temporary expenses (new mode) */}
              {params.id === "new" && tempSupplierExpenses.length > 0 && (
                <List
                  bordered
                  className="matrixList"
                  dataSource={tempSupplierExpenses}
                  renderItem={(item: any, index: number) => {
                    const category = expenseCategories.find(
                      (c) => c._id === item.categoryId,
                    );

                    return (
                      <List.Item
                        actions={[
                          <Button
                            danger
                            size="small"
                            onClick={() =>
                              removeSupplierExpense(undefined, index)
                            }
                          >
                            {t("Delete")}
                          </Button>,
                        ]}
                      >
                        <Text style={{ flex: 1 }}>
                          {category?.name || item.categoryName || t("N/A")} /{" "}
                          {item.supplierName} - {item.amount} RSD{" "}
                          {item.other && (
                            <span className="textAccent">({t("Other")})</span>
                          )}
                        </Text>
                      </List.Item>
                    );
                  }}
                />
              )}
            </div>

            <div className="newSystemDivider">
              <div className="newSystemDivider__line" />
              <div className="newSystemDivider__label">
                {t("Odboci iz dnevnog pazara")}
              </div>
              <div className="newSystemDivider__line newSystemDivider__line--reverse" />
            </div>

            <div className="newSystemSectionLabel">{t("Gotovinske isplate")}</div>
            <div className="cashOutBlock">
              <div className="cashOutComposer">
                <Input
                  placeholder={t("Description")}
                  value={newExpense.description}
                  onChange={(e) =>
                    setNewExpense((prev: any) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
                <Input
                  type="number"
                  placeholder={t("Amount (RSD)")}
                  value={newExpense.total}
                  onChange={(e) =>
                    setNewExpense((prev: any) => ({
                      ...prev,
                      total: e.target.value,
                    }))
                  }
                />
                <Checkbox
                  checked={newExpense.other}
                  className="dailyRevenueOther"
                  onChange={(e) =>
                    setNewExpense((prev: any) => ({
                      ...prev,
                      other: e.target.checked,
                    }))
                  }
                >
                  {t("Other")}
                </Checkbox>
                <Button type="dashed" onClick={addExpense}>
                  {t("Add cash out")}
                </Button>
              </div>

              {cashRegisterList.length > 0 && (
                <List
                  className="cashOutList"
                  bordered
                  dataSource={cashRegisterList}
                  renderItem={(item: any, index) => (
                    <List.Item
                      actions={[
                        <Button
                          danger
                          size="small"
                          onClick={() => removeExpense(index)}
                        >
                          {t("Delete")}
                        </Button>,
                      ]}
                    >
                      <Text>
                        {item.description} - {item.total} RSD{" "}
                        {item?.other && (
                          <span className="textAccent">({t("Other")})</span>
                        )}
                      </Text>
                    </List.Item>
                  )}
                />
              )}
            </div>
          </div>
        )}

        {revenueTypes?.length > 0 && (() => {
          const diff = revenueByTypeTotal - paymentChannelsTotal;
          const isMatch = Math.abs(diff) < 1;
          const bothFilled = paymentChannelsTotal > 0 || revenueByTypeTotal > 0;
          if (!bothFilled) return null;
          return (
            <div className={`drd-diff-banner${isMatch ? " drd-diff-banner--ok" : diff > 0 ? " drd-diff-banner--over" : " drd-diff-banner--under"}`}>
              <div className="drd-diff-banner__rows">
                <div className="drd-diff-banner__row">
                  <span className="drd-diff-banner__label">{t("Payment channels")}</span>
                  <span className="drd-diff-banner__val">{paymentChannelsTotal.toLocaleString("sr-RS")} RSD</span>
                </div>
                <div className="drd-diff-banner__row">
                  <span className="drd-diff-banner__label">{t("Revenue by type")}</span>
                  <span className="drd-diff-banner__val">{revenueByTypeTotal.toLocaleString("sr-RS")} RSD</span>
                </div>
                <div className="drd-diff-banner__divider" />
                <div className="drd-diff-banner__row drd-diff-banner__row--total">
                  <span className="drd-diff-banner__label">
                    {isMatch ? t("Match") : t("Difference")}
                  </span>
                  <span className="drd-diff-banner__diff">
                    {isMatch
                      ? "✓"
                      : `${diff > 0 ? "+" : ""}${diff.toLocaleString("sr-RS")} RSD`}
                  </span>
                </div>
              </div>
              {!isMatch && (
                <div className="drd-diff-banner__hint">
                  {diff > 0
                    ? t("Revenue by type is higher than payment channels")
                    : t("Payment channels are higher than revenue by type")}
                </div>
              )}
            </div>
          );
        })()}

        {revenueTypes?.length > 0 && (
          <div className="dailyRevenueCard">
            <div className="dailyRevenueCard__header">
              <div>
                <div className="dailyRevenueCard__eyebrow">
                  {t("Breakdown")}
                </div>
                <div className="dailyRevenueCard__title">
                  {t("Revenue by Type")}
                </div>
                <p className="dailyRevenueCard__description">
                  {t("Match each type with the amount it generated.")}
                </p>
              </div>
            </div>

            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t("Sva polja su obavezna. Unesite 0 ako nije bilo prihoda za tu grupu.")}
            />

            <div className="revenueTypeGrid">
              {revenueTypes.map((type: any) => {
                const val = revenueTypeValues[type._id];
                const isEmpty = val === undefined || val === null;
                return (
                  <Form.Item
                    key={type._id}
                    label={<span>{type.name} <span style={{ color: "#ff4d4f" }}>*</span></span>}
                    validateStatus={isEmpty ? "error" : ""}
                    help={isEmpty ? t("Obavezno polje — unesite 0 ako nema prihoda") : ""}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      placeholder="0"
                      value={val ?? null}
                      onChange={(v) =>
                        handleRevenueTypeChange(type._id, String(v ?? ""))
                      }
                    />
                  </Form.Item>
                );
              })}
            </div>
          </div>
        )}

        <div className="dailyRevenueCard">
          <div className="dailyRevenueCard__header">
            <div>
              <div className="dailyRevenueCard__eyebrow">{t("Wrap up")}</div>
              <div className="dailyRevenueCard__title">{t("Notes & save")}</div>
            </div>
          </div>

          <Form.Item
            label={t("Notes")}
            name="notes"
            rules={[{ required: false, message: t("Please enter notes") }]}
          >
            <TextArea
              rows={3}
              placeholder={t("Anything extra for this shift?")}
            />
          </Form.Item>
          <div className="dailyRevenueActions">
            <Button onClick={() => navigate(-1)} disabled={isSubmitting}>
              {t("Cancel")}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {t("Save")}
            </Button>
          </div>
        </div>
      </Form>

      <ManageExtraIncomeModal
        visible={isExtraIncomeModalOpen}
        onClose={() => setIsExtraIncomeModalOpen(false)}
        dateRange={extraIncomeDateRange}
        locationId={watchLocation}
        onUpdate={() => {
          fetchExtraIncomeCount();
        }}
      />
    </div>
  );
};

export default DailyRevenueDetails;
